// @givmo/app-admin — company-fundraiser COVER image upload + preview (EP-WEB3).
//
// Pick an image → PREVIEW it locally (object URL) → upload via `fundraisersUploadFundraiserPicture`
// (a single multipart POST, `body:{ file }` — the sdk's `formDataBodySerializer` builds the form-data
// body and clears the Content-Type so the browser sets the multipart boundary) → the computed
// `image_url` refreshes on the fundraiser. Mirrors the charity `LogoUploader` (CPA-WEB6). Client-side
// type/size validation (SEC-BE10 accept: png/jpeg/webp) is FAST FEEDBACK; the backend re-validates and
// its error is surfaced on failure. FCM-WEB18: `readOnly` (a closed or taken-down fundraiser, whose cover
// the server refuses to change) shows the cover as it stands, or "No cover image.", with no control.

import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { fundraisersUploadFundraiserPictureMutation } from '@givmo/api-client/query';
import { isGivmoError } from '@givmo/api-client';
import { Button, Icon, testIdProp, toast } from '@givmo/design-system';
import { ImagePlus, UploadCloud } from 'lucide-react';
import {
  COVER_ACCEPT,
  COVER_MAX_BYTES,
  COVER_TOO_LARGE_MESSAGE,
  COVER_WRONG_TYPE_MESSAGE,
  LIFECYCLE_COPY,
  isAcceptableCover,
} from './company-fundraisers-decode';

const PREFIX = 'company-fundraiser-cover';

export interface CoverImageInputProps {
  fundraiserId: string;
  /** The current computed cover URL (null when none yet). */
  imageUrl: string | null | undefined;
  /** Called after a successful upload so the parent can refetch the fundraiser (fresh `image_url`). */
  onUploaded: () => void | Promise<unknown>;
  /** Optional — disable the picker (e.g. while another mutation holds the fundraiser). */
  disabled?: boolean;
  /** The cover can't change (a closed or taken-down fundraiser): preview only, no control. */
  readOnly?: boolean;
  /** Offered a failed upload first; returns true when it handled the refusal (a stale page). */
  onRefused?: (error: unknown) => boolean;
}

export function CoverImageInput({
  fundraiserId,
  imageUrl,
  onUploaded,
  disabled,
  readOnly = false,
  onRefused,
}: CoverImageInputProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  useEffect(() => {
    // Revoke the previous object URL whenever it changes or the component unmounts (no blob leak).
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const upload = useMutation({
    ...fundraisersUploadFundraiserPictureMutation(),
    onSuccess: async () => {
      setFileError(null);
      await onUploaded();
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      toast.success('Cover image updated.');
    },
    onError: (error) => {
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      if (onRefused?.(error)) return;
      toast.error(
        isGivmoError(error) ? error.message : 'We couldn’t upload that image. Please try again.',
      );
    },
  });

  function onPick(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-picking the same file
    if (!file) return;
    if (!isAcceptableCover(file)) {
      setFileError(COVER_WRONG_TYPE_MESSAGE);
      return;
    }
    if (file.size > COVER_MAX_BYTES) {
      setFileError(COVER_TOO_LARGE_MESSAGE);
      return;
    }
    setFileError(null);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    upload.mutate({ path: { fundraiser_id: fundraiserId }, body: { file } });
  }

  // The in-flight local preview wins, else the server cover, else a neutral placeholder.
  const shownSrc = previewUrl ?? imageUrl ?? null;

  // Read-only with no cover says so, rather than drawing an "add image" placeholder.
  if (readOnly && shownSrc === null)
    return (
      <p className="text-s13 text-text-secondary" {...testIdProp(`${PREFIX}-none`)}>
        {LIFECYCLE_COPY.noCover}
      </p>
    );

  return (
    <div className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-row`)}>
      <div
        className="flex h-[140px] w-full max-w-[360px] items-center justify-center overflow-hidden rounded-lg border border-border-subtle bg-surface-sunken"
        {...testIdProp(`${PREFIX}-preview`)}
      >
        {shownSrc !== null ? (
          <img src={shownSrc} alt="Fundraiser cover" className="h-full w-full object-cover" />
        ) : (
          <Icon icon={ImagePlus} size={28} aria-hidden className="text-text-tertiary" />
        )}
      </div>
      {!readOnly && (
        <>
          <div className="flex items-center gap-8">
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              disabled={disabled}
              loading={upload.isPending}
              leadingIcon={<Icon icon={imageUrl ? UploadCloud : ImagePlus} size={14} aria-hidden />}
              onClick={() => inputRef.current?.click()}
              testId={`${PREFIX}-button`}
            >
              {imageUrl ? 'Replace cover' : 'Upload cover'}
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept={COVER_ACCEPT}
              className="hidden"
              disabled={disabled}
              onChange={onPick}
              {...testIdProp(`${PREFIX}-input`)}
            />
          </div>
          <p className="text-s12 text-text-tertiary">
            A wide PNG, JPG, or WebP works best. Shown on the fundraiser page.
          </p>
        </>
      )}
      {fileError ? (
        <p className="text-s12 text-danger-600" {...testIdProp(`${PREFIX}-file-error`)}>
          {fileError}
        </p>
      ) : null}
    </div>
  );
}
